import Head from 'next/head'
import Link from 'next/link'
import Script from 'next/script'
import { Breadcrumb } from '@/commons/Breadcrumb'
import { buildBreadcrumb, breadcrumbJsonLd } from 'utils/breadcrumb'
import { organizationNode, WEBSITE_ID, SITE } from 'utils/local-business-schema'
import { reviewedIso } from 'utils/article-date-time'
import { s3Static } from 'utils/constants'
import { whatsAppDisplay, whatsAppUrl } from 'utils/business-info'
import { loadAirconLocalityStats } from 'utils/load-aircon-locality-stats'
import { hasAirconLocalityStats, getBusiestLocalities, bandFloor } from 'data/aircon-locality-stats'
import { airconRequestTrends2026 } from 'data/aircon-request-trends-2026'

/**
 * Singapore Aircon Servicing Report - StringsSG's annual, citable data page.
 *
 * Two datasets, kept separate because they measure different things:
 *  - REQUESTS by type (everything customers asked for), from the 2026 trends
 *    post - data/aircon-request-trends-2026.js
 *  - JOBS: bookings with an assigned technician, by area, service mix and cost,
 *    live from /public/GetAirconLocalityStats (snapshot fallback) - the same
 *    data as the 26 locality pages. Bands are floored, shares rounded to 5%,
 *    areas under 20 jobs unpublished; the report inherits all of that.
 *
 * The URL is stable on purpose: links earned by one year's report keep
 * working. Each year, bump REPORT_YEAR / CONTENT_REVIEWED and refresh the
 * request figures; the job figures refresh themselves.
 *
 * Every sentence below is computed from the data or states a definition - no
 * claim the data does not support.
 */

const REPORT_YEAR = 2026
const PUBLISHED = '2026-09-27'
// When this page's content last actually changed. Bump it by hand when you edit the copy.
const CONTENT_REVIEWED = '2026-09-27'

const PATH = '/sg/aircon-servicing-singapore/singapore-aircon-servicing-report'
const PAGE_URL = `${SITE}${PATH}`
const H1 = `Singapore Aircon Servicing Report ${REPORT_YEAR}`
const TITLE = `Singapore Aircon Servicing Report ${REPORT_YEAR} | StringsSG Data`
const IMAGE = s3Static + 'blog/living/aircon/chinese-aircon-service-man-smiling.jpg'

export async function getStaticProps() {
    const stats = await loadAirconLocalityStats()
    const ready = hasAirconLocalityStats(stats)

    return {
        props: {
            jobs: ready ? {
                asOf: stats.asOf,
                windowStart: stats.windowStart,
                minSample: stats.minSample,
                unplacedShare: stats.unplacedShare,
                singapore: stats.singapore,
                localities: stats.localities,
                busiest: getBusiestLocalities(stats),
            } : null,
            requests: airconRequestTrends2026,
        },
        revalidate: 86400,
    }
}

const monthYear = (value) => {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' })
}
const lower = (label) => label.toLowerCase()
const listJoin = (items) => items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0] ?? ''
const localityHref = (name) => `/sg/aircon-servicing-singapore/locality/${encodeURIComponent(name)}`
const formatShare = (share) => share < 0.1 ? '<0.1%' : `${share}%`

// Everything the page states about jobs, derived once from the aggregates.
const summarise = (jobs) => {
    const sg = jobs.singapore
    const published = jobs.localities.filter((r) => r.ready && r.jobsBand)
        .sort((a, b) => bandFloor(b.jobsBand) - bandFloor(a.jobsBand) || a.name.localeCompare(b.name))
    const withCost = published.filter((r) => r.medianOneTimeCost)
        .sort((a, b) => a.medianOneTimeCost - b.medianOneTimeCost)
    return {
        sg,
        published,
        unpublished: jobs.localities.filter((r) => !(r.ready && r.jobsBand)).map((r) => r.name),
        cheapest: withCost[0],
        dearest: withCost[withCost.length - 1],
        since: monthYear(jobs.windowStart),
        asOf: monthYear(jobs.asOf),
    }
}

const buildCsv = (summary) => {
    const rows = [['Area', 'Jobs booked (floored band)', 'Most-booked service', 'Share of bookings (%)', 'Second service', 'Share (%)', 'Typical units per booking', 'Typical one-time booking (SGD)']]
    const row = (r) => [
        r.name, r.jobsBand, r.serviceMix?.[0]?.label ?? '', r.serviceMix?.[0]?.share ?? '',
        r.serviceMix?.[1]?.label ?? '', r.serviceMix?.[1]?.share ?? '', r.medianUnits ?? '', r.medianOneTimeCost ?? '',
    ]
    rows.push(row(summary.sg))
    summary.published.forEach((r) => rows.push(row(r)))
    return rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
}

const buildJsonLd = (jobs, summary) => {
    const trail = buildBreadcrumb(PATH, H1)
    const graph = [
        {
            '@type': 'Report',
            '@id': `${PAGE_URL}/#report`,
            headline: H1,
            name: TITLE,
            description: `StringsSG's annual data on aircon servicing in Singapore: what customers request, what they book, what a typical booking costs, and how it varies across ${summary ? summary.published.length : 'Singapore\'s'} areas.`,
            url: PAGE_URL,
            image: IMAGE,
            inLanguage: 'en-SG',
            datePublished: reviewedIso(PUBLISHED),
            dateModified: reviewedIso(CONTENT_REVIEWED),
            author: { '@id': organizationNode['@id'] },
            publisher: { '@id': organizationNode['@id'] },
            isPartOf: { '@id': WEBSITE_ID },
            mainEntityOfPage: PAGE_URL,
            about: { '@id': `${PAGE_URL}/#data` },
        },
        breadcrumbJsonLd(trail, PAGE_URL),
        organizationNode,
    ]
    if (jobs) {
        graph.push({
            '@type': 'Dataset',
            '@id': `${PAGE_URL}/#data`,
            name: `StringsSG aircon servicing jobs in Singapore by area, ${REPORT_YEAR}`,
            description: 'Aircon servicing jobs booked through StringsSG in Singapore with an assigned technician, aggregated by area: jobs booked (floored band), share of bookings by service, typical units per booking and typical one-time booking cost. Areas with fewer than 20 jobs are not published.',
            url: `${PAGE_URL}#areas`,
            creator: { '@id': organizationNode['@id'] },
            isAccessibleForFree: true,
            temporalCoverage: `${jobs.windowStart}/${String(jobs.asOf).slice(0, 10)}`,
            spatialCoverage: { '@type': 'Place', name: 'Singapore' },
            variableMeasured: [
                'Aircon jobs booked (floored band)',
                'Share of bookings by service (%)',
                'Typical aircon units per booking',
                'Typical one-time booking cost (SGD)',
            ],
            keywords: ['aircon servicing', 'Singapore', 'air conditioner maintenance', 'home services data'],
        })
    }
    return { '@context': 'https://schema.org', '@graph': graph }
}

const Bars = ({ rows, format }) => (
    <ul className="space-y-2">
        {rows.map((row) => (
            <li key={row.label} className="grid grid-cols-[10rem_1fr_4rem] md:grid-cols-[13rem_1fr_5rem] items-center gap-3 text-sm">
                <span className="text-gray-700">{row.label}</span>
                <span className="h-2.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                    <span className="block h-full rounded-full bg-orange-500" style={{ width: `${Math.max(row.share, 0.5)}%` }} />
                </span>
                <span className="text-right font-semibold text-gray-900 tabular-nums">{format(row)}</span>
            </li>
        ))}
    </ul>
)

const Section = ({ id, title, children }) => (
    <section id={id} aria-labelledby={`${id}-title`} className="space-y-4 scroll-mt-20">
        <h2 id={`${id}-title`} className="text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
        {children}
    </section>
)

export default function SingaporeAirconServicingReport({ jobs, requests }) {
    const summary = jobs ? summarise(jobs) : null
    const sg = summary?.sg
    const topRequests = requests.byType
    const csvHref = summary ? `data:text/csv;charset=utf-8,${encodeURIComponent(buildCsv(summary))}` : null
    const reviewed = new Date(`${CONTENT_REVIEWED}T00:00:00+08:00`).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })
    const description = summary
        ? `${sg.jobsBand} aircon jobs and ${requests.totalRequests.toLocaleString('en-SG')} requests: what Singapore books, what a typical aircon servicing costs, and how it varies across ${summary.published.length} areas. StringsSG's annual data report.`
        : `What Singapore requests and books for aircon servicing, from ${requests.totalRequests.toLocaleString('en-SG')} requests. StringsSG's annual data report.`

    const addOns = sg ? sg.serviceMix.slice(1, 3).map((s) => `${lower(s.label)} (${s.share}%)`) : []

    return (
        <>
            <Head>
                <title>{TITLE}</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={description} />
                <meta name="author" content="StringsSG" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={TITLE} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:image" content={IMAGE} />
                <meta property="article:published_time" content={reviewedIso(PUBLISHED)} />
                <meta property="article:modified_time" content={reviewedIso(CONTENT_REVIEWED)} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={TITLE} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={IMAGE} />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={PAGE_URL} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildJsonLd(jobs, summary)) }} />
            </Head>

            <div className="max-w-4xl mx-auto px-4 pt-4 pb-16">
                <Breadcrumb pageTitle={H1} jsonLd={false} />

                <header className="py-8 space-y-4">
                    <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide">StringsSG data report</p>
                    <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">{H1}</h1>
                    <p className="text-gray-600 leading-7 text-base md:text-lg">
                        What Singapore asks for, what it books and what it pays for aircon servicing - drawn from StringsSG&apos;s
                        own booking records, and broken down by area.
                    </p>
                    <p className="text-xs text-gray-500">
                        Published by StringsSG · Last reviewed {reviewed}
                        {summary?.asOf ? ` · Job data as of ${summary.asOf}` : ''}
                    </p>
                </header>

                {summary && (
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
                        {[
                            { label: `Aircon jobs booked since ${summary.since}`, value: sg.jobsBand },
                            { label: 'Bookings that include general servicing', value: `${sg.serviceMix[0].share}%` },
                            { label: 'Typical one-time booking', value: `$${sg.medianOneTimeCost}` },
                            { label: 'Typical units per booking', value: String(sg.medianUnits) },
                        ].map((tile) => (
                            <div key={tile.label} className="rounded-xl border border-orange-200/60 bg-orange-50/40 p-4">
                                <dt className="text-xs text-gray-600">{tile.label}</dt>
                                <dd className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-1">{tile.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                <div className="space-y-14">
                    <Section id="key-findings" title="Key findings">
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-7">
                            {summary && <>
                                <li>{sg.jobsBand} aircon jobs were booked through StringsSG with an assigned technician between {summary.since} and {summary.asOf}.</li>
                                <li>General servicing is included in {sg.serviceMix[0].share}% of bookings. The most common other services are {listJoin(addOns)}.</li>
                                <li>A typical one-time booking covers {sg.medianUnits} units and comes to about ${sg.medianOneTimeCost}, transport included.</li>
                                <li>{sg.oneTimeShare}% of bookings are one-off services; {sg.packageShare}% are multi-visit packages.</li>
                                {jobs.busiest.length > 0 && <li>The busiest areas for aircon bookings are {listJoin(jobs.busiest)}.</li>}
                                {summary.cheapest && summary.dearest && summary.cheapest.name !== summary.dearest.name &&
                                    <li>The typical one-time booking ranges from about ${summary.cheapest.medianOneTimeCost} in {summary.cheapest.name} to about ${summary.dearest.medianOneTimeCost} in {summary.dearest.name}.</li>}
                            </>}
                            <li>
                                Of {requests.totalRequests.toLocaleString('en-SG')} aircon service requests analysed, {topRequests[0].share}% were
                                for {lower(topRequests[0].label)}, followed by {lower(topRequests[1].label)} ({topRequests[1].share}%)
                                and {lower(topRequests[2].label)} ({topRequests[2].share}%).
                            </li>
                        </ul>
                    </Section>

                    {summary && (
                        <Section id="services" title="What Singapore books">
                            <p className="text-gray-700 leading-7">
                                Share of StringsSG aircon bookings that included each service, {summary.since} to {summary.asOf}. A
                                booking can include more than one service, so the shares add up to more than 100%.
                            </p>
                            <Bars rows={sg.serviceMix} format={(r) => `${r.share}%`} />
                        </Section>
                    )}

                    <Section id="requests" title="What Singapore asks for">
                        <p className="text-gray-700 leading-7">
                            {requests.totalRequests.toLocaleString('en-SG')} aircon service requests received by StringsSG, by
                            type - including requests that did not go on to become a booking. From our{' '}
                            <Link href={requests.source} prefetch={false} className="text-orange-600 hover:underline">
                                2026 aircon servicing trends analysis
                            </Link>.
                        </p>
                        <Bars rows={topRequests} format={(r) => formatShare(r.share)} />
                        <table className="w-full text-sm mt-4">
                            <caption className="sr-only">Aircon service requests by type</caption>
                            <thead>
                                <tr className="text-left text-gray-500 border-b border-gray-200">
                                    <th className="py-2 font-medium">Request type</th>
                                    <th className="py-2 font-medium text-right">Requests</th>
                                    <th className="py-2 font-medium text-right">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topRequests.map((r) => (
                                    <tr key={r.label} className="border-b border-gray-100">
                                        <td className="py-2 text-gray-700">{r.label}</td>
                                        <td className="py-2 text-right tabular-nums text-gray-900">{r.requests.toLocaleString('en-SG')}</td>
                                        <td className="py-2 text-right tabular-nums text-gray-900">{formatShare(r.share)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>

                    {summary && (
                        <Section id="areas" title="Aircon servicing by area">
                            <p className="text-gray-700 leading-7">
                                StringsSG aircon jobs by area, busiest first. Each area links to its own page with the full
                                breakdown.
                                {summary.unpublished.length > 0 && ` ${listJoin(summary.unpublished)} had fewer than ${jobs.minSample} jobs, so ${summary.unpublished.length === 1 ? 'its figures are' : 'their figures are'} not published.`}
                            </p>
                            <div className="overflow-x-auto -mx-4 px-4">
                                <table className="w-full min-w-[40rem] text-sm">
                                    <caption className="sr-only">Aircon servicing jobs by area</caption>
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b border-gray-200">
                                            <th className="py-2 pr-3 font-medium">Area</th>
                                            <th className="py-2 pr-3 font-medium text-right">Jobs booked</th>
                                            <th className="py-2 pr-3 font-medium">Most-booked after general servicing</th>
                                            <th className="py-2 pr-3 font-medium text-right">Typical units</th>
                                            <th className="py-2 font-medium text-right">Typical one-time booking</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b border-gray-200 bg-orange-50/40 font-semibold">
                                            <td className="py-2 pr-3 text-gray-900">All of Singapore</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{sg.jobsBand}</td>
                                            <td className="py-2 pr-3">{sg.serviceMix[1] ? `${sg.serviceMix[1].label} (${sg.serviceMix[1].share}%)` : '-'}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{sg.medianUnits ?? '-'}</td>
                                            <td className="py-2 text-right tabular-nums">{sg.medianOneTimeCost ? `$${sg.medianOneTimeCost}` : '-'}</td>
                                        </tr>
                                        {summary.published.map((r) => (
                                            <tr key={r.name} className="border-b border-gray-100">
                                                <td className="py-2 pr-3">
                                                    <Link href={localityHref(r.name)} prefetch={false} className="text-orange-600 hover:underline">{r.name}</Link>
                                                </td>
                                                <td className="py-2 pr-3 text-right tabular-nums text-gray-900">{r.jobsBand}</td>
                                                <td className="py-2 pr-3 text-gray-700">{r.serviceMix?.[1] ? `${r.serviceMix[1].label} (${r.serviceMix[1].share}%)` : '-'}</td>
                                                <td className="py-2 pr-3 text-right tabular-nums text-gray-900">{r.medianUnits ?? '-'}</td>
                                                <td className="py-2 text-right tabular-nums text-gray-900">{r.medianOneTimeCost ? `$${r.medianOneTimeCost}` : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {csvHref && (
                                <a href={csvHref} download={`singapore-aircon-servicing-report-${REPORT_YEAR}.csv`} className="inline-block text-sm text-orange-600 font-semibold hover:underline">
                                    Download this table (CSV)
                                </a>
                            )}
                        </Section>
                    )}

                    <Section id="methodology" title="Methodology">
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-7 text-sm md:text-base">
                            {summary && <>
                                <li><strong>Jobs</strong> are aircon requests booked through StringsSG that were assigned a technician, from {summary.since} to {summary.asOf}, whatever happened to the booking afterwards.</li>
                                <li><strong>Job counts</strong> are shown as floored bands (&quot;250+&quot; means at least 250), and <strong>shares</strong> are rounded to the nearest 5%. A share is the percentage of bookings that included that service.</li>
                                <li><strong>Typical</strong> figures are medians: units across bookings that included servicing, and cost across priced one-time bookings (transport included).</li>
                                <li><strong>Areas</strong> are assigned from each job&apos;s service address - by the estate or street name where it names one, otherwise by its postal sector. About {jobs.unplacedShare}% of jobs could not be placed in an area; they count towards the Singapore-wide figures only. Jobs in areas without their own page count towards Singapore only.</li>
                                <li>An area, and each figure within it, is published only when at least {jobs.minSample} bookings stand behind it.</li>
                            </>}
                            <li><strong>Requests</strong> are every aircon service request StringsSG received in its 2026 trends analysis, including those that did not become a booking, so they are a different measure from jobs.</li>
                            <li>Figures are refreshed from StringsSG&apos;s booking records; the job figures on this page update automatically.</li>
                        </ul>
                    </Section>

                    <Section id="cite" title="How to cite this report">
                        <p className="text-gray-700 leading-7">
                            Journalists, researchers and publishers are welcome to cite figures from this report, with a link to
                            this page as the source.
                        </p>
                        <blockquote className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                            StringsSG ({REPORT_YEAR}). <em>{H1}</em>. {PAGE_URL}
                        </blockquote>
                        <p className="text-gray-700 leading-7 text-sm">
                            For media enquiries or a data request, message us on WhatsApp at{' '}
                            <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{whatsAppDisplay}</a>.
                        </p>
                    </Section>

                    <Section id="related" title="Related">
                        <ul className="list-disc pl-6 space-y-1.5 text-gray-700">
                            <li><Link href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore" prefetch={false} className="text-orange-600 hover:underline">Aircon servicing prices in Singapore</Link></li>
                            <li><Link href={requests.source} prefetch={false} className="text-orange-600 hover:underline">Aircon servicing trends in Singapore, 2026</Link></li>
                            <li><Link href="/sg/aircon-servicing-singapore/how-stringssg-works" prefetch={false} className="text-orange-600 hover:underline">How StringsSG aircon servicing works</Link></li>
                            <li><Link href="/sg/aircon-servicing-singapore" prefetch={false} className="text-orange-600 hover:underline">Book aircon servicing</Link></li>
                        </ul>
                    </Section>
                </div>
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
        </>
    )
}
